/**
 * Dynamic UI Configuration Service
 * Fetches UI configurations from backend and applies them dynamically
 */

import api from './api';
import { getStorageJSON, setStorageJSON } from '../utils/capacitorStorage';

class DynamicUIConfigService {
  constructor() {
    this.config = null;
    this.configVersion = null;
    this.lastFetchTime = null;
    this.fetchInterval = null;
    this.listeners = [];
    this.cacheDuration = 3600000; // 1 hour cache
  }

  /**
   * Initialize service
   */
  async initialize() {
    console.log('DynamicUIConfigService: Initializing...');

    // Load cached config
    await this.loadCachedConfig();

    // Fetch latest config
    await this.fetchConfig();

    // Set up periodic refresh
    this.startPeriodicRefresh();
  }

  /**
   * Load cached configuration
   */
  async loadCachedConfig() {
    try {
      const cached = await getStorageJSON('uiConfig');
      const cachedVersion = await getStorageJSON('uiConfigVersion');
      const cachedTime = await getStorageJSON('uiConfigFetchTime');

      if (cached && cachedVersion && cachedTime) {
        // Check if cache is still valid
        const age = Date.now() - cachedTime;
        if (age < this.cacheDuration) {
          this.config = cached;
          this.configVersion = cachedVersion;
          this.lastFetchTime = cachedTime;
          console.log('DynamicUIConfigService: Loaded cached config version', cachedVersion);
          this.notifyListeners('config_loaded', cached);
          return true;
        }
      }
    } catch (error) {
      console.error('DynamicUIConfigService: Error loading cached config:', error);
    }
    return false;
  }

  /**
   * Fetch configuration from backend
   */
  async fetchConfig() {
    try {
      console.log('DynamicUIConfigService: Fetching config from backend...');

      const response = await api.get('/app/ui-config/', {
        params: {
          current_version: this.configVersion,
          platform: 'android',
        },
      });

      const newConfig = response.data;

      // Check if config has changed
      if (newConfig.version !== this.configVersion) {
        console.log('DynamicUIConfigService: New config version available:', newConfig.version);
        
        this.config = newConfig.config;
        this.configVersion = newConfig.version;
        this.lastFetchTime = Date.now();

        // Cache config
        await setStorageJSON('uiConfig', this.config);
        await setStorageJSON('uiConfigVersion', this.configVersion);
        await setStorageJSON('uiConfigFetchTime', this.lastFetchTime);

        // Notify listeners
        this.notifyListeners('config_updated', this.config);
      } else {
        console.log('DynamicUIConfigService: Config is up to date');
      }

      return this.config;
    } catch (error) {
      console.error('DynamicUIConfigService: Error fetching config:', error);
      
      // If fetch fails, use cached config if available
      if (!this.config) {
        await this.loadCachedConfig();
      }
      
      return this.config;
    }
  }

  /**
   * Get configuration value
   */
  getConfig(path, defaultValue = null) {
    if (!this.config) {
      return defaultValue;
    }

    const keys = path.split('.');
    let value = this.config;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return defaultValue;
      }
    }

    return value;
  }

  /**
   * Get theme configuration
   */
  getTheme() {
    return this.getConfig('theme', {
      primaryColor: '#0ea5e9',
      secondaryColor: '#1890ff',
      backgroundColor: '#ffffff',
    });
  }

  /**
   * Get UI component configuration
   */
  getComponentConfig(componentName) {
    return this.getConfig(`components.${componentName}`, {});
  }

  /**
   * Get feature flags
   */
  getFeatureFlags() {
    return this.getConfig('features', {});
  }

  /**
   * Check if feature is enabled
   */
  isFeatureEnabled(featureName) {
    const features = this.getFeatureFlags();
    return features[featureName] === true;
  }

  /**
   * Get menu configuration
   */
  getMenuConfig() {
    return this.getConfig('menu', []);
  }

  /**
   * Get screen configuration
   */
  getScreenConfig(screenName) {
    return this.getConfig(`screens.${screenName}`, {});
  }

  /**
   * Start periodic config refresh
   */
  startPeriodicRefresh() {
    if (this.fetchInterval) {
      clearInterval(this.fetchInterval);
    }

    // Refresh every 30 minutes
    this.fetchInterval = setInterval(() => {
      this.fetchConfig().catch(console.error);
    }, 1800000);
  }

  /**
   * Force refresh configuration
   */
  async forceRefresh() {
    this.lastFetchTime = null;
    return await this.fetchConfig();
  }

  /**
   * Add config listener
   */
  addListener(callback) {
    this.listeners.push(callback);
    
    // Immediately call with current config if available
    if (this.config) {
      try {
        callback('config_loaded', this.config);
      } catch (error) {
        console.error('Error in config listener:', error);
      }
    }
    
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event, data) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Error in config listener:', error);
      }
    });
  }

  /**
   * Get current config
   */
  getCurrentConfig() {
    return {
      config: this.config,
      version: this.configVersion,
      lastFetchTime: this.lastFetchTime,
    };
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.fetchInterval) {
      clearInterval(this.fetchInterval);
      this.fetchInterval = null;
    }
    this.listeners = [];
  }
}

// Singleton instance
const dynamicUIConfigService = new DynamicUIConfigService();

export default dynamicUIConfigService;

